import React, { useState, useEffect, useMemo } from 'react';
import type { CoinData } from '../types/interfaces';

interface DonateModalProps {
  isOpen: boolean;
  onClose: () => void;
  cryptoData: CoinData[];
}

declare global {
  interface Window {
    bsvPayments?: {
      requestPayment: (action: string, amount: number) => Promise<any>;
    };
  }
}

const PRESET_AMOUNTS = [
  { label: 'Coffee ☕', dollars: 1 },
  { label: 'Lunch 🍕', dollars: 5 },
  { label: 'Dinner 🍽️', dollars: 10 },
  { label: 'Generous 🎁', dollars: 50 },
];

export default function DonateModal({ isOpen, onClose, cryptoData }: DonateModalProps) {
  const [selectedDollarAmount, setSelectedDollarAmount] = useState<number | null>(null);
  const [customDollarAmount, setCustomDollarAmount] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Get BSV price from crypto data
  const bsvPrice = useMemo(() => {
    const bsv = cryptoData.find(coin => coin.id === 'bitcoin-sv' || coin.symbol.toLowerCase() === 'bsv');
    return bsv?.current_price || 50; // Fallback to $50 if BSV price not available
  }, [cryptoData]);

  // Convert dollars to satoshis
  const dollarsToSatoshis = (dollars: number): number => {
    const bsvAmount = dollars / bsvPrice;
    return Math.round(bsvAmount * 100000000);
  };

  // Convert satoshis to dollars
  const satoshisToDollars = (satoshis: number): number => {
    const bsvAmount = satoshis / 100000000;
    return bsvAmount * bsvPrice;
  };

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setSelectedDollarAmount(null);
      setCustomDollarAmount('');
      setIsProcessing(false);
      setError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  const handleDonate = async () => {
    const dollarAmount = selectedDollarAmount || (customDollarAmount ? parseFloat(customDollarAmount) : null);
    
    if (!dollarAmount || dollarAmount <= 0) {
      setError('Please select or enter a donation amount');
      return;
    }

    if (dollarAmount < 0.01) {
      setError('Minimum donation is $0.01');
      return;
    }

    const satoshiAmount = dollarsToSatoshis(dollarAmount);
    
    if (satoshiAmount < 100) {
      setError('Minimum donation is 100 satoshis');
      return;
    }

    if (!window.bsvPayments || !window.bsvPayments.requestPayment) {
      setError('Connect MetaNet wallet');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await window.bsvPayments.requestPayment('DONATE', satoshiAmount);
      
      if (result) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError('Donation was cancelled or failed. Please try again.');
        setIsProcessing(false);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred. Please try again.';
      // Provide more helpful error messages
      if (errorMessage.includes('Wallet not found') || errorMessage.includes('CONNECTION_REFUSED')) {
        setError('MetaNet wallet not found. Please make sure the MetaNet wallet is running and try again.');
      } else {
        setError(errorMessage);
      }
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  const dollarAmount = selectedDollarAmount || (customDollarAmount ? parseFloat(customDollarAmount) : null);
  const satoshiAmount = dollarAmount ? dollarsToSatoshis(dollarAmount) : 0;
  const amountInBSV = satoshiAmount ? (satoshiAmount / 100000000).toFixed(8) : '0';

  return (
    <div
      className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-10 max-w-lg w-[90%] max-h-[90vh] overflow-y-auto relative shadow-bubble-lg border-2 border-primary/20"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-8 top-8 w-12 h-12 rounded-full bg-bg border-2 border-border text-3xl font-bold text-text-secondary cursor-pointer transition-all duration-300 hover:bg-danger hover:border-danger hover:text-white hover:scale-110 hover:rotate-90 active:scale-95 flex items-center justify-center z-10"
        >
          &times;
        </button>

        <div className="text-center mb-8">
          <div className="text-6xl mb-4">💝</div>
          <h2 className="text-3xl mb-3 font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
            Support CryptoScope
          </h2>
          <p className="text-text-secondary text-sm">
            Help me keep this project running and improving! Your support means the world to me. 🌟
          </p>
        </div>

        {success ? (
          <div className="text-center py-8">
            <div className="text-6xl mb-4 animate-bounce">🎉</div>
            <div className="text-2xl font-bold text-green-400 mb-2">Thank You!</div>
            <div className="text-text-secondary">Your donation was successful!</div>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <label className="block mb-4 text-text-secondary font-bold uppercase tracking-wider text-sm">
                💰 Quick Donate Amounts
              </label>
              <div className="grid grid-cols-2 gap-3">
                {PRESET_AMOUNTS.map((preset) => (
                  <button
                    key={preset.dollars}
                    onClick={() => {
                      setSelectedDollarAmount(preset.dollars);
                      setCustomDollarAmount('');
                      setError(null);
                    }}
                    className={`px-5 py-4 rounded-2xl border-2 transition-all duration-300 font-semibold ${
                      selectedDollarAmount === preset.dollars
                        ? 'bg-gradient-to-r from-primary to-primary-light border-primary text-white shadow-bubble-lg scale-105'
                        : 'bg-bg border-border text-text hover:border-primary hover:scale-105'
                    }`}
                  >
                    <div className="text-lg">{preset.label}</div>
                    <div className="text-xl font-bold mt-1">
                      ${preset.dollars.toFixed(2)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block mb-3 text-text-secondary font-bold uppercase tracking-wider text-sm">
                ✨ Custom Amount
              </label>
              <div className="relative">
                <span className="absolute left-6 top-1/2 -translate-y-1/2 text-text-secondary text-lg font-bold">$</span>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={customDollarAmount}
                  onChange={(e) => {
                    setCustomDollarAmount(e.target.value);
                    setSelectedDollarAmount(null);
                    setError(null);
                  }}
                  className="w-full pl-12 pr-6 py-4 bg-bg border-2 border-border rounded-full text-text text-base font-medium transition-all duration-300 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20 focus:shadow-bubble placeholder:text-text-secondary/50"
                />
                {dollarAmount && (
                  <div className="absolute right-6 top-1/2 -translate-y-1/2 text-text-secondary text-xs">
                    ≈ {amountInBSV} BSV
                  </div>
                )}
              </div>
              <p className="text-text-secondary/70 text-xs mt-2 ml-2">
                Minimum: $0.01
              </p>
            </div>

            {error && (
              <div className="mb-6 bg-gradient-to-br from-red-500/10 to-red-600/10 border-2 border-red-500/50 rounded-3xl p-4 text-red-400 text-sm font-medium shadow-bubble">
                ⚠️ {error}
              </div>
            )}

            {dollarAmount && dollarAmount >= 0.01 && (
              <div className="mb-6 bg-gradient-to-br from-primary/10 to-purple-500/10 border-2 border-primary/30 rounded-3xl p-5 shadow-bubble">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-text-secondary font-semibold">Donation Amount:</span>
                  <span className="text-2xl font-bold text-primary">
                    ${dollarAmount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-text-secondary">In BSV:</span>
                  <span className="font-bold text-primary">
                    {amountInBSV} BSV
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs mt-1">
                  <span className="text-text-secondary/70">Satoshis:</span>
                  <span className="text-text-secondary/70">
                    {satoshiAmount.toLocaleString()} sats
                  </span>
                </div>
              </div>
            )}

            <button
              onClick={handleDonate}
              disabled={!dollarAmount || dollarAmount < 0.01 || isProcessing}
              className="w-full py-4 bg-gradient-to-r from-primary to-purple-500 border-none rounded-full text-white text-base font-bold cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-bubble-lg hover:shadow-primary/50 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isProcessing ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⚙️</span>
                  Processing...
                </span>
              ) : (
                '💝 Donate Now'
              )}
            </button>

            <p className="text-text-secondary/70 text-xs text-center mt-6">
              All donations go directly to supporting CryptoScope development. Thank you! 🙏
            </p>
          </>
        )}
      </div>
    </div>
  );
}

